import { useState, useEffect } from 'react';
import axios from 'axios';
import { Crown, TrendingUp, Users, Package, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { API } from '@/App';
import { logClientError } from '@/utils/clientError';

const ExpressDashboard = () => {
  const [stats, setStats] = useState({
    totalSubscribers: 0,
    activeSubscribers: 0,
    expiredSubscribers: 0,
    pendingSubscribers: 0,
    totalRevenueFCFA: 0,
    totalRevenueUSD: 0,
    expressDeliveries: 0
  });
  const [recentSubscriptions, setRecentSubscriptions] = useState([]);
  const [expiringSoon, setExpiringSoon] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load subscriptions
      const subsResponse = await axios.get(`${API}/admin/subscriptions`);
      const subscriptions = subsResponse.data;

      // Load deliveries
      const deliveriesResponse = await axios.get(`${API}/admin/deliveries/validated?is_express=true`);
      const expressDeliveries = deliveriesResponse.data;

      // Calculate stats
      const active = subscriptions.filter(s => s.status === 'active');
      const expired = subscriptions.filter(s => s.status === 'expired');
      const pending = subscriptions.filter(s => s.status === 'pending');

      const revenueFCFA = subscriptions
        .filter(s => s.status === 'active' && s.currency === 'FCFA')
        .reduce((sum, s) => sum + s.amount, 0);

      const revenueUSD = subscriptions
        .filter(s => s.status === 'active' && s.currency === 'USD')
        .reduce((sum, s) => sum + s.amount, 0);

      // Find expiring soon (within 7 days)
      const now = new Date();
      const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      const expiring = active.filter(s => {
        if (!s.end_date) return false;
        const endDate = new Date(s.end_date);
        return endDate >= now && endDate <= sevenDaysFromNow;
      });

      setStats({
        totalSubscribers: subscriptions.length,
        activeSubscribers: active.length,
        expiredSubscribers: expired.length,
        pendingSubscribers: pending.length,
        totalRevenueFCFA: revenueFCFA,
        totalRevenueUSD: revenueUSD,
        expressDeliveries: expressDeliveries.length
      });

      setRecentSubscriptions(subscriptions.slice(0, 5));
      setExpiringSoon(expiring);
    } catch (error) {
      logClientError('admin.express_dashboard.load', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center p-12"><div className="spinner"></div></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex items-center justify-center">
          <Crown className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-3xl font-bold">Tableau de bord Coli Noki Pro</h2>
          <p className="text-gray-600">Vue d’ensemble des abonnements premium</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-t-4 border-t-amber-500">
          <CardHeader className="pb-3">
            <CardDescription>Abonnés Actifs</CardDescription>
            <CardTitle className="text-3xl">{stats.activeSubscribers}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">Sur {stats.totalSubscribers} total</p>
          </CardContent>
        </Card>

        <Card className="border-t-4 border-t-green-500">
          <CardHeader className="pb-3">
            <CardDescription>Revenus FCFA</CardDescription>
            <CardTitle className="text-2xl">{stats.totalRevenueFCFA.toLocaleString()}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">Abonnements actifs</p>
          </CardContent>
        </Card>

        <Card className="border-t-4 border-t-blue-500">
          <CardHeader className="pb-3">
            <CardDescription>Revenus USD</CardDescription>
            <CardTitle className="text-2xl">${stats.totalRevenueUSD.toLocaleString()}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">Abonnements actifs</p>
          </CardContent>
        </Card>

        <Card className="border-t-4 border-t-purple-500">
          <CardHeader className="pb-3">
            <CardDescription>Livraisons express</CardDescription>
            <CardTitle className="text-3xl">{stats.expressDeliveries}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">En cours</p>
          </CardContent>
        </Card>
      </div>

      {/* Pending & Expiring */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Validations */}
        {stats.pendingSubscribers > 0 && (
          <Card className="border-2 border-amber-200 bg-amber-50">
            <CardHeader>
              <CardTitle className="text-amber-900 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Validations en attente
              </CardTitle>
              <CardDescription className="text-amber-700">
                {stats.pendingSubscribers} abonnement(s) à valider
              </CardDescription>
            </CardHeader>
            <CardContent>
              <a href="/admin/subscriptions">
                <button className="text-sm text-amber-800 hover:text-amber-900 font-medium underline">
                  Voir les abonnements en attente →
                </button>
              </a>
            </CardContent>
          </Card>
        )}

        {/* Expiring Soon */}
        {expiringSoon.length > 0 && (
          <Card className="border-2 border-orange-200 bg-orange-50">
            <CardHeader>
              <CardTitle className="text-orange-900 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Expirent bientôt
              </CardTitle>
              <CardDescription className="text-orange-700">
                {expiringSoon.length} abonnement(s) expirent dans les 7 jours
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {expiringSoon.slice(0, 3).map((sub) => (
                  <div key={sub.id} className="flex items-center justify-between text-sm">
                    <span className="font-medium">{sub.vendeur_name}</span>
                    <span className="text-orange-700">
                      {new Date(sub.end_date).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Recent Subscriptions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-cyan-600" />
            Derniers abonnements
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recentSubscriptions.map((sub) => (
              <div key={sub.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">{sub.vendeur_name}</p>
                  <p className="text-sm text-gray-600">
                    {sub.plan === 'starter_1m' && 'Starter (1 mois)'}
                    {sub.plan === 'pro_3m' && 'Pro (3 mois)'}
                    {sub.plan === 'business_12m' && 'Business (12 mois)'}
                  </p>
                </div>
                <div className="text-right">
                  <Badge className={
                    sub.status === 'active' ? 'bg-green-100 text-green-700' :
                    sub.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                    'bg-gray-100 text-gray-700'
                  }>
                    {sub.status === 'active' && 'Actif'}
                    {sub.status === 'pending' && 'En attente'}
                    {sub.status === 'expired' && 'Expiré'}
                  </Badge>
                  <p className="text-sm text-gray-600 mt-1">
                    {sub.amount} {sub.currency}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ExpressDashboard;
